import React, { useEffect, useState, useCallback, useRef } from 'react';
import useRacePlayback from '../useRacePlayback';
import AdminKeyGate from '../components/AdminKeyGate';
import VideoOverlay from '../components/VideoOverlay';
import RacePodium from '../components/RacePodium';
import TimingTower from '../components/pitwall/TimingTower';
import PredictionStandings from '../components/pitwall/PredictionStandings';
import '../components/pitwall/PitwallVisual.css';
import { getVisualPreview } from '../pitwallVisualPreview';
import { setAdminKey, getAdminRaces, getLiveLeaderboard, getRaceLeaderboard } from '../api';

// Visual-only Pitwall 2. Keep all polling/clock semantics as in the supplied
// page adapter; map data now belongs exclusively to /pitwall3.
const POLL_INTERVAL = 15_000;

const kf = `
  @keyframes pwpulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.3; transform: scale(0.6); }
  }
  @keyframes redflag {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.80; }
  }
  @keyframes rowIn {
    from { opacity: 0; transform: translateX(-6px); }
    to   { opacity: 1; transform: translateX(0); }
  }
`;

function bannerConfig(trackStatus) {
  if (!trackStatus || trackStatus === 'clear') return null;
  if (trackStatus === 'red')                   return { bg: '#c00020', fg: '#fff', pulse: true };
  if (trackStatus === 'sc' || trackStatus === 'sc_ending') return { bg: '#92400e', fg: '#fef3c7', pulse: false };
  if (trackStatus === 'vsc' || trackStatus === 'vsc_ending') return { bg: '#1e3a5f', fg: '#bfdbfe', pulse: false };
  if (trackStatus === 'chequered')             return { bg: '#1a1a1a', fg: '#d4d4d4', pulse: false };
  return null;
}

function elapsedTime(raceDate) {
  const ms = Date.now() - new Date(raceDate);
  if (ms < 0) return 'LIVE';
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  const s = Math.floor((ms % 60_000) / 1_000);
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${m}:${String(s).padStart(2, '0')}`;
}

// ── outer shell: admin gate only ──────────────────────────────────────────────
export default function PitWallPageV2() {
  const isPreview = new URLSearchParams(window.location.search).has('preview');
  const [adminKey, setAdminKeyState] = useState(() => {
    const stored = sessionStorage.getItem('f1tips_adminkey');
    if (stored && !isPreview) setAdminKey(stored);
    return isPreview ? null : stored;
  });

  const clearKey = () => {
    sessionStorage.removeItem('f1tips_adminkey');
    setAdminKey(null);
    setAdminKeyState(null);
  };

  // Preview uses fixtures exclusively; do not send admin credentials or requests.
  if (isPreview) return <Dashboard isPreview onAuthError={() => {}} />;

  if (!adminKey) {
    return (
      <div style={{
        minHeight: '100vh', background: '#080808',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <AdminKeyGate onKey={k => { setAdminKey(k); setAdminKeyState(k); }} />
      </div>
    );
  }

  return <Dashboard isPreview={false} onAuthError={clearKey} />;
}

// ── dashboard (all hooks live here) ──────────────────────────────────────────
function Dashboard({ onAuthError, isPreview }) {
  const preview = getVisualPreview(window.location.search);
  const [liveRace, setLiveRace]     = useState(isPreview ? preview.race : null);
  const [raceLoading, setRaceLoading] = useState(!isPreview);
  const [authError, setAuthError]   = useState(false);
  const [liveData, setData]         = useState(isPreview ? preview.data : null);
  const playback = useRacePlayback(isPreview ? null : liveRace?.id);
  const pubMode = !isPreview && playback.mode !== 'live';
  // The manually played pre-race greeting does not depend on telemetry readiness.
  const canShowVideo = !pubMode || (liveRace?.locked === false &&
    !liveRace.cancelled && !liveRace.official_top10);
  const data = pubMode ? (playback.frame?.dashboard ? {
    ...playback.frame.dashboard, liveTop10: playback.frame.dashboard.top10 || [],
    leaderboard: playback.frame.dashboard.predictionProjection === 'delayed' ? playback.frame.dashboard.leaderboard || [] : [], totalLaps: playback.frame.race.totalLaps,
    updatedAt: new Date(playback.frame.targetMs).toISOString(),
  } : null) : liveData;
  const [elapsed, setElapsed]       = useState('--:--');
  const [countdown, setCountdown]   = useState(POLL_INTERVAL / 1000);
  const [showVideo, setShowVideo]   = useState(false);
  const [showPodium, setShowPodium] = useState(false);
  const [podiumLeaderboard, setPodiumLeaderboard] = useState([]);
  const revealedRaceIdRef = useRef(null);

  // Find the active race: prefer locked race in live window, fall back to any
  // locked race without results, then to any race with a video_url set (pre-race).
  useEffect(() => {
    if (isPreview) return undefined;
    getAdminRaces().then(races => {
      const inWindow = races.find(r => {
        if (!r.locked || r.official_top10) return false;
        if (!r.race_date) return false;
        const h = (Date.now() - new Date(r.race_date)) / 36e5;
        return h >= 0 && h < 12;
      });
      const lockedFallback = races.find(r => r.locked && !r.official_top10);
      const videoFallback = races.find(r => !r.cancelled && !r.official_top10 && r.video_url);
      setLiveRace(inWindow ?? lockedFallback ?? videoFallback ?? null);
    }).catch(e => {
      if (e.response?.status === 401) setAuthError(true);
    }).finally(() => setRaceLoading(false));
  }, [isPreview]);

  const openPodium = useCallback((raceId) => {
    getRaceLeaderboard(raceId).then(lb => {
      setPodiumLeaderboard(lb.filter(r => !r.is_staff));
      setShowPodium(true);
    }).catch(() => {});
  }, []);

  // Poll the pinned race for results being posted (initial load only fetches the race list once,
  // so this is what lets Pit Wall notice results posted from another tab and auto-reveal the podium)
  useEffect(() => {
    if (isPreview || !liveRace?.id) return undefined;
    const raceId = liveRace.id;
    const check = () => {
      getAdminRaces().then(races => {
        const fresh = races.find(r => r.id === raceId);
        if (!fresh) return;
        setLiveRace(prev => (prev && prev.id === raceId ? { ...prev, ...fresh } : prev));
        if (!pubMode && fresh.official_top10 && revealedRaceIdRef.current !== raceId) {
          revealedRaceIdRef.current = raceId;
          openPodium(raceId);
        }
      }).catch(e => {
        if (e.response?.status === 401) setAuthError(true);
      });
    };
    check();
    const id = setInterval(check, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [isPreview, liveRace?.id, openPodium, pubMode]);

  // Elapsed timer (ticks every second)
  useEffect(() => {
    if (isPreview || !liveRace?.race_date) { if (isPreview) setElapsed('32:00'); return undefined; }
    const tick = () => setElapsed(elapsedTime(liveRace.race_date));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isPreview, liveRace?.race_date]);

  // Countdown to next refresh; fixture previews keep a deterministic value.
  useEffect(() => {
    if (isPreview) return undefined;
    const id = setInterval(() => setCountdown(c => (c <= 1 ? POLL_INTERVAL / 1000 : c - 1)), 1000);
    return () => clearInterval(id);
  }, [isPreview]);

  // Live data fetch
  const fetchData = useCallback(() => {
    if (isPreview || pubMode || !liveRace?.id) return;
    getLiveLeaderboard(liveRace.id)
      .then(d => { setData(d); setCountdown(POLL_INTERVAL / 1000); })
      .catch(() => {});
  }, [isPreview, pubMode, liveRace?.id]);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchData]);

  // ── loading / no race ────────────────────────────────────────────────────
  if (raceLoading) {
    return (
      <div style={{ minHeight: '100vh', background: '#080808', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: '#333', letterSpacing: '0.12em' }}>INITIALISING PIT WALL...</p>
      </div>
    );
  }

  if (authError) {
    return (
      <div style={{ minHeight: '100vh', background: '#080808', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
        <p style={{ fontFamily: 'var(--font-display)', fontSize: 40, fontWeight: 800, color: '#e8002d', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Admin Key Rejected</p>
        <p style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: '#444' }}>Your stored admin key is invalid or expired</p>
        <button
          onClick={onAuthError}
          style={{
            background: '#f9b000', border: 'none', borderRadius: 4,
            color: '#111', fontFamily: 'var(--font-mono)', fontSize: 12,
            fontWeight: 700, letterSpacing: '0.1em', padding: '10px 18px',
            cursor: 'pointer', marginTop: 4,
          }}
        >
          RE-ENTER KEY
        </button>
      </div>
    );
  }

  if (!liveRace) {
    return (
      <div style={{ minHeight: '100vh', background: '#080808', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
        <p style={{ fontFamily: 'var(--font-display)', fontSize: 40, fontWeight: 800, color: '#f9b000', textTransform: 'uppercase', letterSpacing: '0.1em' }}>No Live Race</p>
        <p style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: '#444' }}>Pit Wall activates when a race is locked by admin</p>
      </div>
    );
  }

  const latestControl = data?.raceControl?.slice(-1)[0] ?? null;
  const lastScMsg     = data?.raceControl?.slice().reverse().find(m => m.category === 'SafetyCar' || m.category === 'Vsc');
  const scStale       = lastScMsg && ((pubMode && playback.frame ? playback.frame.targetMs : Date.now()) - new Date(lastScMsg.date).getTime()) > 3 * 60 * 1000;
  const effectiveStatus = scStale ? 'clear' : data?.trackStatus;
  const banner        = bannerConfig(effectiveStatus);
  const bannerH       = banner ? 68 : 0;

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: '#080808',
      backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 27px, rgba(255,255,255,0.016) 28px)',
      display: 'grid',
      gridTemplateRows: `${bannerH}px 68px minmax(0, 1fr) 52px`,
      overflow: 'hidden',
      transition: 'grid-template-rows 0.35s ease',
    }}>
      <style>{kf}</style>
      {canShowVideo && showVideo && liveRace?.video_url && (
        <VideoOverlay url={liveRace.video_url} onClose={() => setShowVideo(false)} />
      )}
      {!pubMode && showPodium && liveRace?.official_top10 && (
        <RacePodium
          race={liveRace}
          leaderboard={podiumLeaderboard}
          onClose={() => setShowPodium(false)}
        />
      )}

      {/* Red left-edge stripe */}
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: '#e8002d', zIndex: 10 }} />

      {/* ── RACE CONTROL BANNER ───────────────────────────────────── */}
      <div style={{
        overflow: 'hidden',
        background: banner?.bg ?? 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: banner?.pulse ? 'redflag 1.4s ease-in-out infinite' : 'none',
      }}>
        {banner && latestControl && (
          <span style={{
            fontFamily: 'var(--font-display)',
            fontSize: 30, fontWeight: 800,
            letterSpacing: '0.16em', textTransform: 'uppercase',
            color: banner.fg,
          }}>
            {latestControl.message}
          </span>
        )}
      </div>

      {/* ── HEADER ───────────────────────────────────────────────── */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        padding: '0 28px 0 20px',
        borderBottom: '1px solid #181818',
        background: '#0c0c0c',
      }}>
        {/* Race name */}
        <div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#e8002d', letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 2 }}>
            {liveRace.season} · Round {liveRace.round}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: '#f0f0f0', textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1 }}>
              {liveRace.name}
            </div>
            {canShowVideo && liveRace.video_url && (
              <button
                onClick={() => setShowVideo(true)}
                style={{
                  background: '#e8002d', border: 'none', borderRadius: 4,
                  color: '#fff', fontFamily: 'var(--font-mono)', fontSize: 10,
                  fontWeight: 700, letterSpacing: '0.1em', padding: '4px 10px',
                  cursor: 'pointer', flexShrink: 0,
                }}
              >
                ▶ VIDEO
              </button>
            )}
            {!pubMode && liveRace.official_top10 && (
              <button
                onClick={() => openPodium(liveRace.id)}
                style={{
                  background: '#f9b000', border: 'none', borderRadius: 4,
                  color: '#111', fontFamily: 'var(--font-mono)', fontSize: 10,
                  fontWeight: 700, letterSpacing: '0.1em', padding: '4px 10px',
                  cursor: 'pointer', flexShrink: 0,
                }}
              >
                🏆 PODIUM
              </button>
            )}
          </div>
        </div>

        {/* Lap counter + race time, side by side, same size */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 28 }}>
          {data?.currentLap && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#444', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 0 }}>
                Lap
              </div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 44, fontWeight: 800, color: '#f9b000', lineHeight: 1, letterSpacing: '-0.02em' }}>
                {data.currentLap}
                {data?.totalLaps && <span style={{ color: '#555' }}> / {data.totalLaps}</span>}
              </div>
            </div>
          )}
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#444', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 0 }}>
              {pubMode ? 'Race clock' : data?.currentLap ? 'Time' : 'Elapsed'}
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 44, fontWeight: 800, color: data?.currentLap ? '#ccc' : '#f9b000', lineHeight: 1, letterSpacing: '-0.02em' }}>
              {pubMode ? '—' : elapsed}
            </div>
          </div>
        </div>

        {/* Weather */}
        <div style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 13 }}>
          {data?.weather ? (
            <>
              <span style={{ color: '#555' }}>TRACK </span>
              <span style={{ color: '#e0e0e0', fontWeight: 700 }}>{Math.round(data.weather.trackTemp)}°C</span>
              <span style={{ color: '#333', margin: '0 10px' }}>·</span>
              <span style={{ color: '#555' }}>AIR </span>
              <span style={{ color: '#e0e0e0', fontWeight: 700 }}>{Math.round(data.weather.airTemp)}°C</span>
              <span style={{ color: '#333', margin: '0 10px' }}>·</span>
              <span style={{ color: data.weather.rainfall > 0 ? '#3b82f6' : '#444', fontWeight: 700 }}>
                {data.weather.rainfall > 0 ? 'WET' : 'DRY'}
              </span>
            </>
          ) : (
            <span style={{ color: '#2a2a2a' }}>— weather —</span>
          )}
        </div>
      </div>

      {/* Visual-only components. Keep the adapter's data shape and clock intact. */}
      <div className="pitwall2-main">
        <TimingTower data={data} hidePoints={pubMode} modeLabel={pubMode ? `TV SYNC · ${(playback.frame?.dashboardStatus || playback.status).toUpperCase()}` : 'LIVE'} />
        {pubMode && playback.frame?.dashboard?.predictionProjection !== 'delayed'
          ? <div className="pw2-empty">Waiting for delayed prediction standings</div>
          : <PredictionStandings leaderboard={data?.leaderboard ?? []} />}
      </div>

      {/* ── PIT STOP FOOTER ───────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16,
        padding: '0 24px 0 20px',
        borderTop: '1px solid #161616',
        background: '#0c0c0c',
        overflow: 'hidden',
      }}>
        <span style={{ fontFamily: 'var(--font-display)', fontSize: 10, fontWeight: 700, color: '#e8002d', letterSpacing: '0.18em', textTransform: 'uppercase', flexShrink: 0 }}>
          Pit Stops
        </span>

        <div style={{ display: 'flex', gap: 10, flex: 1, overflow: 'hidden', alignItems: 'center' }}>
          {data?.recentPits?.length > 0
            ? data.recentPits.map((pit, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  background: '#111', borderRadius: 4, padding: '5px 12px',
                  borderLeft: `3px solid ${pit.teamColour ? '#' + pit.teamColour : '#333'}`,
                  flexShrink: 0,
                }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 14, color: '#e0e0e0' }}>{pit.acronym}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: '#f9b000', fontWeight: 700 }}>{Number(pit.duration).toFixed(1)}s</span>
                  {pit.lap && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#444' }}>L{pit.lap}</span>}
                </div>
              ))
            : <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: '#222' }}>No pit stops yet</span>
          }
        </div>

        {/* Refresh countdown */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f9b000', display: 'inline-block', animation: 'pwpulse 1.4s ease-in-out infinite' }} />
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#333' }}>
            {countdown}s
          </span>
        </div>
      </div>
    </div>
  );
}
